import React from 'react';
import { useTranslation } from 'react-i18next';
import { CallState } from '../../types';
import { ConferenceParticipants } from './ConferenceParticipants';
import {
  CallDisplayContext,
  getCallPrimaryDisplay,
  getCallSecondaryDisplay,
  getCallerInitial,
  resolveCallDirection,
} from './call-card.types';
import styles from './CallCard.module.css';

interface CallCardHeaderProps {
  callState: CallState;
  callDisplayContext?: CallDisplayContext;
  isRinging: boolean;
  isOnHold: boolean;
  isActive: boolean;
  isConnecting?: boolean;
  callError: string | null;
}

function getStatusText(
  t: (key: string) => string,
  options: {
    isRinging: boolean;
    isOnHold: boolean;
    isActive: boolean;
    isConnecting: boolean;
    isConference: boolean;
    direction: ReturnType<typeof resolveCallDirection>;
  }
): string {
  if (options.isRinging) {
    return options.direction === 'OUTBOUND'
      ? t('call:status.outboundCall')
      : t('call:status.incomingCall');
  }
  if (options.isConnecting) {
    return t('call:status.connecting');
  }
  if (options.isOnHold) {
    return t('call:status.onHold');
  }
  if (options.isConference) {
    return t('call:status.conference');
  }
  if (options.isActive) {
    return t('call:status.connected');
  }
  return options.direction === 'OUTBOUND' ? t('call:status.outboundCall') : t('call:status.call');
}

export function CallCardHeader({
  callState,
  callDisplayContext,
  isRinging,
  isOnHold,
  isActive,
  isConnecting = false,
  callError,
}: CallCardHeaderProps) {
  const { t } = useTranslation();
  const direction = resolveCallDirection(callState, callDisplayContext);
  const primaryDisplay = getCallPrimaryDisplay(callState, callDisplayContext);
  const secondaryDisplay = getCallSecondaryDisplay(callState, primaryDisplay, callDisplayContext);
  const callerInitial = getCallerInitial(callState, primaryDisplay);
  const statusText = getStatusText(t, {
    isRinging,
    isOnHold,
    isActive,
    isConnecting,
    isConference: callState.isConference,
    direction,
  });

  return (
    <div className={styles.callHeader}>
      <div className={styles.callerAvatar}>{callerInitial}</div>
      <div className={styles.callerNumber}>{primaryDisplay}</div>
      {secondaryDisplay && <div className={styles.callerPhone}>{secondaryDisplay}</div>}
      <div className={styles.directionBadges}>
        <span
          className={`${styles.directionBadge} ${
            direction === 'OUTBOUND' ? styles.directionOutbound : styles.directionInbound
          }`}
        >
          {direction === 'OUTBOUND' ? t('call:direction.outbound') : t('call:direction.inbound')}
        </span>
      </div>
      <div
        className={`${styles.callStatusText} ${isRinging ? styles.ringing : ''} ${callState.isConference ? styles.conference : ''}`}
      >
        {statusText}
      </div>
      {callError && <div className={styles.callError}>{callError}</div>}
      {callState.skillName && <div className={styles.skillBadge}>{callState.skillName}</div>}
      {callState.isConference && <ConferenceParticipants parties={callState.conferenceParties} />}
    </div>
  );
}

export default CallCardHeader;
